import { PersonRegistrationFieldConfig } from '../types/customer/base';

export const STANDARD_FIELD_KEYS = ['cpf', 'email', 'phone', 'date_of_birth', 'rg'] as const;

export const STANDARD_FIELD_LABELS: Record<string, string> = {
  cpf: 'CPF',
  email: 'Email',
  phone: 'Telefone',
  date_of_birth: 'Data de Nascimento',
  rg: 'RG',
};

/** The person types that have their own registration field configuration. */
const CONFIGURABLE_PERSON_TYPES = ['visitor', 'service_provider'] as const;

/**
 * Which person type's field configuration a registration form should render.
 *
 * "Cadastrar Acesso" registers either a visitante or a prestador, and the operator chooses with a
 * toggle INSIDE the form — so the configuration has to follow that toggle, not the prop the modal
 * was opened with. Reading it from the prop alone meant a prestador registration rendered the
 * visitor config, and every custom field configured for prestador was missing.
 *
 * Precedence: what is selected on screen, then what the caller asked for, then visitante.
 */
export function resolveFieldConfigType(
  selectedRoleType?: string,
  personTypeProp?: string,
): string {
  const known = (value?: string) =>
    CONFIGURABLE_PERSON_TYPES.includes(value as typeof CONFIGURABLE_PERSON_TYPES[number]);

  if (known(selectedRoleType)) return selectedRoleType as string;
  if (known(personTypeProp)) return personTypeProp as string;
  return 'visitor';
}

export interface OrderedFieldItem {
  key: string;
  isCustom: boolean;
  order: number;
  col_span: 1 | 2;
  enabled: boolean;
}

/**
 * Merges standard + custom fields into a single sorted list.
 * Falls back to natural index ordering when no explicit order is set.
 * Returns ALL fields (both enabled and disabled); callers should filter as needed.
 */
export function getOrderedFields(config: PersonRegistrationFieldConfig): OrderedFieldItem[] {
  const items: OrderedFieldItem[] = [];

  STANDARD_FIELD_KEYS.forEach((key, naturalIndex) => {
    const def = config.standard_fields?.[key];
    items.push({
      key,
      isCustom: false,
      order: def?.order ?? naturalIndex,
      col_span: def?.col_span ?? 1,
      enabled: def ? def.enabled : true,
    });
  });

  const customFields = config.custom_fields ?? [];
  customFields.forEach((field, naturalIndex) => {
    items.push({
      key: field.id,
      isCustom: true,
      order: field.order ?? (STANDARD_FIELD_KEYS.length + naturalIndex),
      col_span: field.col_span ?? 2,
      enabled: true,
    });
  });

  items.sort((a, b) => a.order - b.order);
  return items;
}

/**
 * The fields "Cadastrar Acesso" draws itself, before the configurable grid.
 *
 * Only CPF — it shares the Nome row there (still honouring its `enabled` flag), so rendering it
 * from the config too would show it twice. Telefone deliberately is NOT here: that form used to
 * hardcode its own copy, which consumed a grid cell and pushed every later field one place along,
 * so the two registration forms on the same screen laid their fields out differently.
 */
export const EXISTING_PERSON_HARDCODED_FIELDS = ['cpf'];

/**
 * The enabled fields a form should render from the admin's configuration, in the admin's order,
 * minus whatever that form already draws for itself.
 *
 * Both registration forms on the Operacional screen share one configuration. The new-visitor form
 * read it; "Cadastrar Acesso" (the same screen, for a person already registered) did not — it
 * hardcoded three fields, so RG, Data de Nascimento and every custom field were missing from it.
 */
export function configurableFieldsFor(
  config: PersonRegistrationFieldConfig,
  options: { exclude?: string[] } = {},
): OrderedFieldItem[] {
  const exclude = options.exclude ?? [];
  return getOrderedFields(config)
    .filter(item => item.enabled)
    .filter(item => !exclude.includes(item.key));
}

/**
 * Writes fresh 0-based order values back into a config after the items have been reordered.
 */
export function applyOrderToConfig(
  config: PersonRegistrationFieldConfig,
  orderedItems: OrderedFieldItem[],
): PersonRegistrationFieldConfig {
  const newStandardFields = { ...(config.standard_fields ?? {}) };
  const newCustomFields = (config.custom_fields ?? []).map(f => ({ ...f }));

  orderedItems.forEach((item, index) => {
    if (!item.isCustom) {
      const existing = newStandardFields[item.key] ?? { enabled: true, required: item.key === 'cpf' };
      newStandardFields[item.key] = { ...existing, order: index };
    } else {
      const fieldIndex = newCustomFields.findIndex(f => f.id === item.key);
      if (fieldIndex !== -1) {
        newCustomFields[fieldIndex] = { ...newCustomFields[fieldIndex], order: index };
      }
    }
  });

  return {
    ...config,
    standard_fields: newStandardFields,
    custom_fields: newCustomFields,
  };
}
